import React from "react";
import { useNavigate } from "react-router-dom";
import "../styles/process-optimization.css";
import {
  getProcessInformation,
  getProcessPermissions,
  getProcessPermissionsCount,
  reviewProcessPermission,
} from "../services/processService";
import { resolveRoleName } from "../utils/auth";
import { toast } from "../utils/toast";
import FormModal from "../components/ui/FormModal";
import ConfirmModal from "../components/ui/ConfirmModal";
import { getProcessVersionHistory } from "../services/processOptimizationService";
import { getMyPendingApprovals, deletePendingApproval } from "../services/userService";

function formatHistoryDateTime(value) {
  if (!value) return { date: "-", time: "-" };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: "-", time: "-" };
  return {
    date: d.toLocaleDateString(),
    time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
  };
}

const ProcessOptimizationListPage = () => {
  const navigate = useNavigate();
  const [processes, setProcesses] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [roleKey, setRoleKey] = React.useState("");

  const [approvalCounts, setApprovalCounts] = React.useState(null);
  const [approvalsOpen, setApprovalsOpen] = React.useState(false);
  const [approvalsLoading, setApprovalsLoading] = React.useState(false);
  const [approvalsError, setApprovalsError] = React.useState("");
  const [approvals, setApprovals] = React.useState([]);

  // historyLoadingCode tracks which process is loading — fixes all-row-disable bug
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyLoadingCode, setHistoryLoadingCode] = React.useState(null);
  const [historyError, setHistoryError] = React.useState("");
  const [historyRows, setHistoryRows] = React.useState([]);
  const [historyProcess, setHistoryProcess] = React.useState({ name: "", code: "" });

  const [userApprovalsOpen, setUserApprovalsOpen] = React.useState(false);
  const [userApprovalsLoading, setUserApprovalsLoading] = React.useState(false);
  const [userApprovalsError, setUserApprovalsError] = React.useState("");
  const [userApprovals, setUserApprovals] = React.useState([]);
  const [deletingId, setDeletingId] = React.useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);

  // Full text of an "Incomplete" reason — too long to sit inside a table cell
  const [reasonDetail, setReasonDetail] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const raw = sessionStorage.getItem("auth") || localStorage.getItem("auth");
        const auth = raw ? JSON.parse(raw) : null;
        const user = auth?.user;
        if (!user) return;
        const rn = await resolveRoleName(user);
        if (!mounted) return;
        const normalized = String(rn || user?.role || "").toUpperCase().replace(/[^A-Z]/g, "");
        setRoleKey(normalized);
      } catch (_) {
        if (mounted) setRoleKey("");
      }
    })();
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const list = await getProcessInformation();
        if (mounted) setProcesses(list);
      } catch (_) {
        if (mounted) setProcesses([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const isAdminish = roleKey === "ADMIN" || roleKey === "SUPERADMIN";

  React.useEffect(() => {
    if (!isAdminish) {
      setApprovalCounts(null);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const counts = await getProcessPermissionsCount();
        if (mounted) setApprovalCounts(counts || null);
      } catch (_) {
        if (mounted) setApprovalCounts(null);
      }
    })();
    return () => { mounted = false; };
  }, [isAdminish]);

  const loadApprovals = React.useCallback(async () => {
    if (!isAdminish) return;
    setApprovalsLoading(true);
    setApprovalsError("");
    try {
      const res = await getProcessPermissions({ permission_status: "PENDING", limit: 50 });
      const items = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setApprovals(items);
    } catch (e) {
      const msg = e?.message || "Failed to load permissions";
      setApprovalsError(msg);
      toast.error(msg);
    } finally {
      setApprovalsLoading(false);
    }
  }, [isAdminish]);

  const handleOpenApprovals = React.useCallback(async () => {
    setApprovalsOpen(true);
    await loadApprovals();
  }, [loadApprovals]);

  const handleReview = React.useCallback(
    async (id, action) => {
      if (!id || !action) return;
      try {
        await reviewProcessPermission(id, {
          permission_status: action,
          remarks:
            action === "APPROVED"
              ? "Approved via process optimization"
              : "Rejected via process optimization",
        });
        toast.success(`Request ${action.toLowerCase()}`);
        await loadApprovals();
        const counts = await getProcessPermissionsCount();
        setApprovalCounts(counts || null);
      } catch (e) {
        toast.error(e?.message || "Action failed");
      }
    },
    [loadApprovals],
  );

  const filteredProcesses = React.useMemo(() => {
    const term = String(searchTerm || "").trim().toLowerCase();
    if (!term) return processes;
    return processes.filter((p) => {
      const name = p.process_name.toLowerCase();
      const code = p.process_code.toLowerCase();
      const company = String(p.company?.name || "").toLowerCase();
      return name.includes(term) || code.includes(term) || company.includes(term);
    });
  }, [processes, searchTerm]);

  const handleOpenHistory = React.useCallback(async (process) => {
    const { process_name, process_code } = process;
    if (!process_code) {
      toast.error("Process code is not available for this process");
      return;
    }

    setHistoryProcess({ name: process_name, code: process_code });
    setHistoryOpen(true);
    setHistoryLoadingCode(process_code);
    setHistoryError("");
    setHistoryRows([]);

    try {
      const res = await getProcessVersionHistory(process_code);
      const items = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setHistoryRows(items);
    } catch (e) {
      const msg = e?.message || "Failed to load version history";
      setHistoryError(msg);
      toast.error(msg);
    } finally {
      setHistoryLoadingCode(null);
    }
  }, []);

  const loadUserApprovals = React.useCallback(async () => {
    setUserApprovalsLoading(true);
    setUserApprovalsError("");
    try {
      const res = await getMyPendingApprovals();
      const items = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setUserApprovals(items);
    } catch (e) {
      if (e?.response?.status === 404 || e?.status === 404) {
        setUserApprovals([]);
      } else {
        const msg = e?.message || "Failed to load approval requests";
        setUserApprovalsError(msg);
        toast.error(msg);
      }
    } finally {
      setUserApprovalsLoading(false);
    }
  }, []);

  const handleOpenUserApprovals = React.useCallback(async () => {
    setUserApprovalsOpen(true);
    await loadUserApprovals();
  }, [loadUserApprovals]);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!confirmDeleteId) return;
    try {
      setDeletingId(confirmDeleteId);
      await deletePendingApproval(confirmDeleteId);
      toast.success("Approval request deleted successfully");
      setConfirmDeleteId(null);
      await loadUserApprovals();
    } catch (e) {
      toast.error(e?.message || "Failed to delete request");
    } finally {
      setDeletingId(null);
    }
  }, [confirmDeleteId, loadUserApprovals]);

  const isFiltering = String(searchTerm || "").trim().length > 0;

  return (
    <div className="page-container pol-page">
      <div className="page-header">
        <div className="header-content pol-header">
          <div>
            <h2 className="page-title">Process Optimization</h2>
            <p className="page-subtitle">Explore optimization insights for your processes</p>
          </div>
          <div className="roles-search pol-toolbar">
            <input
              className="search-input"
              type="text"
              placeholder="Search by process name, code, or company"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              className="secondary-btn sm"
              onClick={() => setSearchTerm("")}
              disabled={loading}
            >
              Clear
            </button>
            {isFiltering && !loading && (
              <span className="pol-result-count" aria-live="polite">
                {filteredProcesses.length} of {processes.length}
              </span>
            )}
            {isAdminish ? (
              <button
                type="button"
                className="primary-btn sm approve-process-btn"
                onClick={handleOpenApprovals}
                disabled={approvalsLoading}
              >
                Approve Process
                {approvalCounts?.pending > 0 && (
                  <span
                    className="approve-process-dot"
                    aria-label={`Pending approvals: ${approvalCounts.pending}`}
                  />
                )}
              </button>
            ) : (
              <button
                type="button"
                className="primary-btn sm view-approvals-btn"
                onClick={handleOpenUserApprovals}
                disabled={userApprovalsLoading}
              >
                View Approval Requests
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="role-card process-optimization-panel pol-panel">
          <table className="panel-table pol-table">
            <thead>
              <tr>
                <th>Process Name</th>
                <th>Company</th>
                <th>Data Status</th>
                <th>Skill Coverage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="pol-skeleton-row" aria-hidden="true">
                    <td>
                      <div className="pol-skeleton pol-skeleton--title" />
                      <div className="pol-skeleton pol-skeleton--code" />
                    </td>
                    <td><div className="pol-skeleton pol-skeleton--text" /></td>
                    <td><div className="pol-skeleton pol-skeleton--pill" /></td>
                    <td><div className="pol-skeleton pol-skeleton--pill" /></td>
                    <td><div className="pol-skeleton pol-skeleton--actions" /></td>
                  </tr>
                ))
              ) : filteredProcesses.length === 0 ? (
                <tr>
                  <td colSpan={5} className="panel-table-empty">
                    <div className="pol-empty">
                      <div className="pol-empty-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="7" />
                          <path d="m20 20-3.5-3.5" />
                        </svg>
                      </div>
                      <div className="pol-empty-title">
                        {isFiltering ? "No matching processes" : "No processes yet"}
                      </div>
                      <div className="pol-empty-desc">
                        {isFiltering
                          ? `Nothing matches “${searchTerm.trim()}”. Try a different name, code, or company.`
                          : "Once processes are created they will appear here, ready to optimize."}
                      </div>
                      {isFiltering && (
                        <button
                          type="button"
                          className="secondary-btn sm"
                          onClick={() => setSearchTerm("")}
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredProcesses.map((process, idx) => {
                  const { process_id, process_name, process_code, process_version, company, is_complete, reason, skill_gap_count, skill_gap_tasks } = process;
                  const incomplete = is_complete === false;
                  const skillGaps = Number(skill_gap_count) || 0;
                  return (
                    <tr key={process_id ?? idx}>
                      <td data-label="Process">
                        <div className="panel-process-name">
                          {process_name || "Unnamed process"}
                        </div>
                        <div className="pol-process-meta">
                          {process_code && (
                            <span className="panel-process-code">{process_code}</span>
                          )}
                          {process_version != null && (
                            <span
                              className="pol-version-badge"
                              title={`Process version ${process_version} — each optimization save creates a new version of the same process code`}
                            >
                              v{process_version}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Company">
                        <div className="panel-process-company">{company?.name || "—"}</div>
                      </td>
                      <td data-label="Data">
                        <div className="pol-status-cell">
                          <span className={`approval-pill pol-status-pill ${incomplete ? "pending" : "approved"}`}>
                            {incomplete ? "Incomplete" : "Complete"}
                          </span>
                          {incomplete && reason && (
                            <button
                              type="button"
                              className="pol-why-btn"
                              title={reason}
                              aria-label={`Why ${process_name || "this process"} is incomplete`}
                              onClick={() =>
                                setReasonDetail({ name: process_name, reason })
                              }
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 16v-4" />
                                <path d="M12 8h.01" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                      <td data-label="Skills">
                        {skillGaps > 0 ? (
                          <span
                            className="pol-flag pol-flag--skill"
                            title={`Advisory — optimization is not blocked. Responsible job lacks a required skill on:\n\n• ${(skill_gap_tasks || []).join("\n• ")}`}
                          >
                            {skillGaps} task{skillGaps === 1 ? "" : "s"}
                          </span>
                        ) : (
                          <span className="pol-none">No gaps</span>
                        )}
                      </td>
                      <td data-label="Actions">
                        <div className="table-action-group">
                          <button
                            type="button"
                            className="primary-btn table-action-btn"
                            disabled={!process_id}
                            title={incomplete && reason ? `Optimize anyway — ${reason}` : undefined}
                            onClick={() => {
                              if (!process_id) return;
                              navigate(
                                `/process-optimization/results/${encodeURIComponent(String(process_id))}`,
                              );
                            }}
                          >
                            <span className="table-action-icon" aria-hidden="true">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M13 2L3 14h7l-1 8 12-14h-7l-1-6z" />
                              </svg>
                            </span>
                            <span>Optimize</span>
                          </button>

                          <button
                            type="button"
                            className="secondary-btn table-history-btn"
                            onClick={() => handleOpenHistory(process)}
                            disabled={!process_code || historyLoadingCode === process_code}
                          >
                            {historyLoadingCode === process_code ? "Loading..." : "History"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        open={historyOpen}
        title="Process History"
        size="default"
        onCancel={() => setHistoryOpen(false)}
        footer={
          <button type="button" className="primary-btn" onClick={() => setHistoryOpen(false)}>
            Close
          </button>
        }
      >
        <div className="history-process-meta">
          <div className="history-process-name">{historyProcess.name || "-"}</div>
          <div className="history-process-code">Code: {historyProcess.code || "-"}</div>
        </div>

        {historyLoadingCode ? (
          <div className="approval-state">Loading...</div>
        ) : historyError ? (
          <div className="approval-state error">{historyError}</div>
        ) : historyRows.length === 0 ? (
          <div className="approval-state">No version history found.</div>
        ) : (
          <table className="approval-table history-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                <th>Date</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.map((row, idx) => {
                const { date, time } = formatHistoryDateTime(row?.created_at);
                return (
                  <tr key={`${row?.process_id ?? "p"}-${row?.process_version ?? idx}-${idx}`}>
                    <td data-label="Version">{row?.process_version ?? "-"}</td>
                    <td data-label="Status">{row?.PROCESS_STATUS || row?.process_status || "-"}</td>
                    <td data-label="Date">{date}</td>
                    <td data-label="Time">{time}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </FormModal>

      <FormModal
        open={approvalsOpen}
        title="Pending Process Approvals"
        size="default"
        onCancel={() => setApprovalsOpen(false)}
        footer={
          <button type="button" className="primary-btn" onClick={() => setApprovalsOpen(false)}>
            Close
          </button>
        }
      >
        <div className="approval-summary">
          <span className="approval-pill">Total: {approvalCounts?.total ?? "—"}</span>
          <span className="approval-pill pending">Pending: {approvalCounts?.pending ?? "—"}</span>
          <span className="approval-pill approved">Approved: {approvalCounts?.approved ?? "—"}</span>
          <span className="approval-pill rejected">Rejected: {approvalCounts?.rejected ?? "—"}</span>
        </div>

        {approvalsLoading ? (
          <div className="approval-state">Loading...</div>
        ) : approvalsError ? (
          <div className="approval-state error">{approvalsError}</div>
        ) : approvals.length === 0 ? (
          <div className="approval-state">No pending requests.</div>
        ) : (
          <table className="approval-table">
            <thead>
              <tr>
                <th>Process</th>
                <th>Company</th>
                <th>Requested By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvals.map((p) => {
                const companyName = p.company?.name || p.company_name || p.companyId || "-";
                const submitter =
                  p.submitter?.name ||
                  p.submitter?.email ||
                  p.submitted_by_name ||
                  p.submitted_by ||
                  "-";
                return (
                  <tr key={p.permission_id || p.id}>
                    <td data-label="Process">
                      <div className="approval-process">{p.process_name || p.name || "-"}</div>
                      {p.process_code && (
                        <div className="approval-process-code">{p.process_code}</div>
                      )}
                    </td>
                    <td data-label="Company">{companyName}</td>
                    <td data-label="Requested By">{submitter}</td>
                    <td data-label="Actions">
                      <div className="approval-actions">
                        <button
                          type="button"
                          className="primary-btn"
                          onClick={() => handleReview(p.permission_id || p.id, "APPROVED")}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="secondary-btn approval-reject"
                          onClick={() => handleReview(p.permission_id || p.id, "REJECTED")}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </FormModal>

      <FormModal
        open={userApprovalsOpen}
        title="My Pending Approval Requests"
        size="default"
        onCancel={() => setUserApprovalsOpen(false)}
        footer={
          <button type="button" className="primary-btn" onClick={() => setUserApprovalsOpen(false)}>
            Close
          </button>
        }
      >
        {userApprovalsLoading ? (
          <div className="approval-state">Loading...</div>
        ) : userApprovalsError ? (
          <div className="approval-state error">{userApprovalsError}</div>
        ) : userApprovals.length === 0 ? (
          <div className="approval-state">No pending requests found.</div>
        ) : (
          <table className="approval-table">
            <thead>
              <tr>
                <th>Process</th>
                <th>Status</th>
                <th>Requested Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {userApprovals.map((p) => {
                const dateVal = p.created_at || p.createdAt || "";
                const formattedDate = dateVal
                  ? new Date(dateVal).toLocaleDateString() +
                    " " +
                    new Date(dateVal).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                  : "-";
                const isDeleting = deletingId === (p.permission_id || p.id);
                return (
                  <tr key={p.permission_id || p.id}>
                    <td data-label="Process">
                      <div className="approval-process">{p.process_name || p.name || "-"}</div>
                      {p.process_code && (
                        <div className="approval-process-code">{p.process_code}</div>
                      )}
                    </td>
                    <td data-label="Status">
                      <span className="approval-pill pending">
                        {p.permission_status || "PENDING"}
                      </span>
                    </td>
                    <td data-label="Requested Date">{formattedDate}</td>
                    <td data-label="Action">
                      <button
                        type="button"
                        className="secondary-btn approval-reject"
                        onClick={() => setConfirmDeleteId(p.permission_id || p.id)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </FormModal>
      <FormModal
        open={!!reasonDetail}
        title="Why this process is incomplete"
        size="default"
        onCancel={() => setReasonDetail(null)}
        footer={
          <button type="button" className="primary-btn" onClick={() => setReasonDetail(null)}>
            Close
          </button>
        }
      >
        <div className="history-process-meta">
          <div className="history-process-name">{reasonDetail?.name || "-"}</div>
        </div>
        <p className="pol-reason-text">{reasonDetail?.reason}</p>
        <p className="pol-reason-note">
          You can still run the optimization — incomplete data may reduce the accuracy of
          the results.
        </p>
      </FormModal>

      <ConfirmModal
        open={!!confirmDeleteId}
        title="Delete Approval Request"
        message="Are you sure you want to delete this approval request? This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDanger
        busy={!!deletingId}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
};

export default ProcessOptimizationListPage;
